/**
 * Line the task's existing rows up under the mapped frame's columns.
 *
 * `GET /ai-ingest/existing-rows` returns the target's rows with the descriptor's FIELD NAMES as columns
 * (`consumption_type`, `country`, …). The mapped `_output` frame names its columns however the agent did
 * ("Consumption Type", "Country"), and the commit builder matches the two by a normalised slug — the
 * payload reports the result as `column_by_field` (field name → frame column). So the frame's columns are
 * the ones the user has been reading, and this re-keys the existing rows onto them.
 *
 * Pure and standalone so it can be tested directly — `src/utils` is a barrel Vitest cannot import (see
 * frontend/CLAUDE.md).
 *
 * The result always has the frame's columns, in the frame's order, when there are any:
 *  - a frame column that maps to a field takes that field's value;
 *  - a frame column the commit ignores (not a template field) is blank on every existing row — the task
 *    does not store it, and inventing a value would be worse than showing none;
 *  - a field the frame has no column for is appended after the frame's columns under its own name, so an
 *    existing row never silently loses a value the task holds.
 * Without frame columns (nothing mapped yet) the existing rows come back with their own field names.
 *
 * @param {{columns: string[], rows: Array<Array<*>>, total_rows: number, page: number, total_pages: number}|undefined} existing
 * @param {string[]|undefined} frameColumns  the mapped output frame's column names
 * @param {Object<string,string>|undefined} columnByField  payload.column_by_field
 * @returns {{columns: string[], rows: Array<Array<*>>, total_rows: number, page: number, total_pages: number}|null}
 */
export function alignExistingRows(existing, frameColumns, columnByField) {
  if (!existing) return null;
  const fields = existing.columns || [];
  const rows = existing.rows || [];
  if (!frameColumns || frameColumns.length === 0) return { ...existing, columns: fields, rows };

  const mapping = columnByField || {};
  // frame column -> field name, and which fields the frame accounts for.
  const fieldByColumn = {};
  const covered = new Set();
  for (const field of fields) {
    // Fall back to the field name itself: a frame that already uses the descriptor's names needs no
    // mapping, and a payload that failed to build has no `column_by_field` at all.
    const column = mapping[field] ?? (frameColumns.includes(field) ? field : null);
    if (column != null && frameColumns.includes(column)) {
      fieldByColumn[column] = field;
      covered.add(field);
    }
  }
  const extraFields = fields.filter((f) => !covered.has(f));
  const indexOfField = Object.fromEntries(fields.map((f, i) => [f, i]));

  const columns = [...frameColumns, ...extraFields];
  const aligned = rows.map((row) => [
    ...frameColumns.map((column) => {
      const field = fieldByColumn[column];
      return field === undefined ? null : row[indexOfField[field]] ?? null;
    }),
    ...extraFields.map((field) => row[indexOfField[field]] ?? null),
  ]);

  return { ...existing, columns, rows: aligned };
}

export default alignExistingRows;
